// Pricing section (US-111) — business nature, pricing models, the products
// sub-list, and the pricing policy. JSX lifted verbatim from BusinessEditor;
// the products list helpers + `togglePricingModel` stay owned by the parent
// (Wave D `useListEditor` wiring) and arrive as props.

import { useT } from '@barel/i18n';
import {
  Button,
  Card,
  FieldAssembly,
  Input,
  Pill,
  Radio,
  RadioGroup,
  Segmented,
  Textarea,
} from '@barel/ui';

import { PRICING_MODEL_KEYS, type BusinessDraft } from '../lib/defaults';
import { SectionHeader } from './SectionHeader';
import type { SetField } from './types';

export interface PricingSectionProps {
  businessNature: BusinessDraft['businessNature'];
  pricingModels: BusinessDraft['pricingModels'];
  pricingProducts: BusinessDraft['pricingProducts'];
  pricingPolicy: BusinessDraft['pricingPolicy'];
  onChange: SetField;
  togglePricingModel: (key: string) => void;
  addProduct: () => void;
  updateProduct: (
    idx: number,
    patch: Partial<{ name: string; price: string; note: string }>,
  ) => void;
  removeProduct: (idx: number) => void;
}

export function PricingSection({
  businessNature,
  pricingModels,
  pricingProducts,
  pricingPolicy,
  onChange,
  togglePricingModel,
  addProduct,
  updateProduct,
  removeProduct,
}: PricingSectionProps) {
  const t = useT('businesses');

  return (
    <Card padding="lg" radius="2xl" data-testid="business-section-pricing">
      <SectionHeader title={t('sections.pricing')} subtitle={t('pricing.subtitle')} />
      <div className="flex flex-col gap-4">
        <FieldAssembly label={t('pricing.natureLabel')}>
          <Segmented
            value={businessNature}
            onValueChange={(value: string) =>
              onChange('businessNature', value as BusinessDraft['businessNature'])
            }
            options={[
              { value: 'service', label: t('pricing.nature.service') },
              { value: 'product', label: t('pricing.nature.product') },
              { value: 'both', label: t('pricing.nature.both') },
            ]}
            size="sm"
            aria-label={t('pricing.natureLabel')}
          />
        </FieldAssembly>
        <FieldAssembly label={t('pricing.modelsLabel')} hint={t('pricing.modelsHint')}>
          <div className="flex flex-wrap gap-2" data-testid="business-pricing-models">
            {PRICING_MODEL_KEYS.map((key) => {
              const selected = pricingModels.includes(key);
              return (
                <Pill
                  key={key}
                  asChild
                  tone={selected ? 'accent' : 'neutral'}
                  variant={selected ? 'solid' : 'outline'}
                  size="sm"
                >
                  <button
                    type="button"
                    onClick={() => togglePricingModel(key)}
                    data-testid={`business-pricing-model-${key}`}
                    data-selected={selected || undefined}
                  >
                    {t(`pricing.model.${key}`)}
                  </button>
                </Pill>
              );
            })}
          </div>
        </FieldAssembly>
        <FieldAssembly label={t('pricing.productsLabel')}>
          <div className="flex flex-col gap-3">
            {pricingProducts.map((product, idx) => (
              <Card key={idx} padding="md" radius="xl">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <FieldAssembly label={t('pricing.productNameLabel')}>
                        <Input
                          type="text"
                          value={product.name}
                          placeholder={t('pricing.productNamePlaceholder')}
                          onChange={(event) => updateProduct(idx, { name: event.target.value })}
                          data-testid={`business-product-name-${idx}`}
                        />
                      </FieldAssembly>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('pricing.removeProduct')}
                      onClick={() => removeProduct(idx)}
                      data-testid={`business-product-remove-${idx}`}
                    >
                      ×
                    </Button>
                  </div>
                  <FieldAssembly label={t('pricing.productPriceLabel')}>
                    <Input
                      type="text"
                      value={product.price}
                      placeholder={t('pricing.productPricePlaceholder')}
                      onChange={(event) => updateProduct(idx, { price: event.target.value })}
                    />
                  </FieldAssembly>
                  <FieldAssembly label={t('pricing.productNoteLabel')}>
                    <Textarea
                      value={product.note}
                      rows={2}
                      placeholder={t('pricing.productNotePlaceholder')}
                      onChange={(event) => updateProduct(idx, { note: event.target.value })}
                    />
                  </FieldAssembly>
                </div>
              </Card>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addProduct}
              data-testid="business-product-add"
            >
              {t('pricing.addProduct')}
            </Button>
          </div>
        </FieldAssembly>
        <FieldAssembly label={t('pricing.policyLabel')}>
          <RadioGroup
            value={pricingPolicy}
            onValueChange={(value) =>
              onChange('pricingPolicy', value as BusinessDraft['pricingPolicy'])
            }
            aria-label={t('pricing.policyLabel')}
          >
            {(['on_demand', 'open', 'never'] as const).map((key) => (
              <label key={key} className="flex items-center gap-2 text-sm text-secondary">
                <Radio value={key} data-testid={`business-pricing-policy-${key}`} />
                {t(`pricing.policy.${key}`)}
              </label>
            ))}
          </RadioGroup>
        </FieldAssembly>
      </div>
    </Card>
  );
}
